import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import isPropValid from "@emotion/is-prop-valid";
import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  lighten,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment-timezone";
import { Controller, useForm } from "react-hook-form";
import ScheduleSelector from "react-schedule-selector";
import { StyleSheetManager } from "styled-components";

import { useKeycloak } from "~/components/authorization";
import { ProviderRegistrationService } from "~/services";
import { routeToErrorPage } from "~/utils/navigationMap";
import {
  mapToProviderGeneralAvailability,
  mapToUiGeneralAvailability,
  PROVIDER_REGISTRATION_TITLE_PROPS,
} from "~/utils/providerRegistrationHelpers";
import { getRegistrationRows, RegistrationRow } from "./ProviderFields.config";
import { withProviderData } from "./withProviderData";

interface ProviderHomeProps {
  providerRegistration: any;
}

const ProviderHome: FC<ProviderHomeProps> = ({ providerRegistration: initialProviderRegistration }) => {
  const theme = useTheme();
  const keycloak = useKeycloak();

  const [isLoading, setIsLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showError, setShowError] = useState(false);

  const abortController = useRef<AbortController>(new AbortController());

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, dirtyFields },
    reset,
    trigger,
    watch,
  } = useForm({
    defaultValues: initialProviderRegistration,
    mode: "onChange",
  });

  const formValues = watch();

  const { updatedDate, publicProvider } = formValues;

  const isRequired = !!publicProvider;

  const handleCloseSchedule = () => setShowSchedule(false);

  const handleSave = async (updatedData: any) => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = new AbortController();
    }

    setIsLoading(true);
    try {
      const updateResponse = await ProviderRegistrationService.updateProviderRegistration(
        keycloak.userState.providerRegistrationId!,
        {
          ...updatedData,
          generalAvailability: mapToProviderGeneralAvailability(updatedData.generalAvailability),
        },
        {
          signal: abortController.current?.signal,
        },
      );
      setIsLoading(false);

      if (updateResponse?.status === 200) {
        reset({
          ...updateResponse.data,
          generalAvailability: mapToUiGeneralAvailability(updateResponse.data.generalAvailability),
        });
      } else {
        setShowError(true);
      }
    } catch {
      routeToErrorPage();
    }
  };

  const getRules = useCallback(
    (requiredMessage = "Required", additionalRules = {}) => ({
      validate: (value: any) => {
        if (isRequired) {
          if (value === null || value === undefined || value === "") {
            return requiredMessage;
          }
        }
        return true;
      },
      ...additionalRules,
    }),
    [isRequired],
  );

  const registrationRows = useMemo(
    (): RegistrationRow[] =>
      getRegistrationRows({
        control,
        dirtyFields,
        getRules,
        updatedDate,
        isRequired,
        setShowSchedule,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [control, dirtyFields, getRules, updatedDate, isRequired, setShowSchedule, formValues],
  );

  useEffect(() => {
    const _abortController = abortController.current;

    return () => {
      _abortController?.abort();
    };
  }, []);

  useEffect(() => {
    trigger();
  }, [trigger, publicProvider]);

  return (
    <Stack alignItems="center" direction="column" flexGrow={1} height="100%" justifyContent="center" padding={2} width="100%">
      <Card elevation={6} sx={{ display: "flex", flexDirection: "column", padding: 4, maxWidth: "lg", width: "100%", height: "100%" }}>
        <Typography align="center" color="custom.main" fontWeight="bold" variant="h4" gutterBottom>
          Welcome, {keycloak.userInfo.name}
        </Typography>
        <Box>
          <Typography align="center" color="custom.main" fontWeight="bold" overflow="auto" whiteSpace="nowrap" gutterBottom>
            Your current information is listed below
            <br />
            Click the &quot;Save Changes&quot; button to save any changes you make
          </Typography>
        </Box>
        <Divider />
        <Stack direction="column" overflow="auto" position="relative">
          <Box overflow="auto" p={1}>
            {registrationRows.map((row, index) => (
              <Box
                key={(typeof row.label === "string" ? row.label : row.key) || index}
                alignItems="center"
                display="flex"
                gap={1}
                justifyContent="space-between"
                px={2}
                py={0.5}
                sx={{
                  backgroundColor: index % 2 !== 0 ? "action.focus" : "transparent",
                  borderRadius: 1,
                }}
              >
                <Typography
                  sx={{
                    color: row.isDirty ? "primary.main" : "text.secondary",
                    fontWeight: row.isDirty ? "bold" : "normal",
                    transition: "all 0.2s ease",
                    width: "50%",
                  }}
                >
                  {row.label}:
                </Typography>
                <Box
                  sx={{
                    textAlign: row.path === "generalAvailability" ? "right" : "left",
                    justifySelf: "end",
                    overflow: "hidden",
                    width: "50%",
                  }}
                >
                  {row.renderEdit ? (
                    row.renderEdit()
                  ) : typeof row.value === "string" || typeof row.value === "number" ? (
                    <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.value}</Typography>
                  ) : (
                    row.value
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          <Divider sx={{ mb: 1 }} />
          <Stack direction="row" gap={10}>
            <Box flexGrow={1} />
            <Button disabled={!isDirty} onClick={() => reset()} variant="outlined">
              Discard Changes
            </Button>
            <Button disabled={!isDirty || !isValid} onClick={handleSubmit(handleSave)} variant="contained">
              Save Changes
            </Button>
            <Box flexGrow={1} />
          </Stack>
          {!!isLoading && (
            <>
              <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, position: "absolute" }} open />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  justifySelf: "center",
                  alignContent: "center",
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
              >
                <CircularProgress size={150} />
              </Box>
            </>
          )}
        </Stack>
        <Dialog maxWidth="md" onClose={handleCloseSchedule} open={showSchedule} scroll="paper" fullWidth>
          <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography {...PROVIDER_REGISTRATION_TITLE_PROPS}>Please indicate your general availability during the week</Typography>
            <Tooltip title="Cancel">
              <IconButton onClick={handleCloseSchedule}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent dividers>
            <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
              <Typography align="left" variant="body2">
                Click to select one time or drag to select multiple times at once. Please scroll down to view later times.
              </Typography>
              <Controller
                control={control}
                defaultValue={[]}
                name="generalAvailability"
                render={({ field }) => (
                  <Box
                    sx={{
                      "& > div:first-of-type > div:first-of-type > div:first-of-type": {
                        position: "sticky",
                        top: theme.spacing(-2),
                        backgroundColor: "background.paper",
                      },
                    }}
                  >
                    <StyleSheetManager shouldForwardProp={(prop) => isPropValid(prop)}>
                      <ScheduleSelector
                        hourlyChunks={2}
                        hoveredColor={lighten(theme.palette.primary.main, 0.4)}
                        maxTime={24}
                        minTime={0}
                        numDays={7}
                        onChange={(newSchedule) => field.onChange(newSchedule)}
                        renderDateLabel={(date) => (
                          <Typography
                            position="sticky"
                            sx={{ backgroundColor: "background.paper" }}
                            textAlign="center"
                            top={theme.spacing(-2)}
                            variant="h6"
                          >
                            {moment(date).format("dddd")}
                          </Typography>
                        )}
                        renderTimeLabel={(time) => <Typography textAlign="end">{moment(time).format("h:mm a")}</Typography>}
                        selectedColor={theme.palette.primary.main}
                        selection={field.value}
                        startDate={new Date(1769990400000)} // schedule will show Sunday-Saturday
                        timeFormat="h:mma"
                        unselectedColor={lighten(theme.palette.primary.light, 0.5)}
                      />
                    </StyleSheetManager>
                  </Box>
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseSchedule} variant="contained">
              Done
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog onClose={() => setShowError(false)} open={showError}>
          <DialogTitle>Error</DialogTitle>
          <DialogContent>
            <DialogContentText>Something went wrong, please try again.</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowError(false)} variant="contained">
              Dismiss
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Stack>
  );
};

export default withProviderData(ProviderHome);
